import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const initialJobs = [
  {
    id: 'senior-ai-engineer',
    title: 'Senior AI Engineer',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Senior',
    description:
      'Lead the development of the AI models behind the clinical computation layer — LLMs, deterministic guideline mapping, and robust ML pipelines for on-prem hospital deployment.',
    postedDate: '2024-11-01',
  },
  {
    id: 'ml-ops-engineer',
    title: 'MLOps Engineer',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Mid',
    description:
      'Build and maintain the ML infrastructure that runs the clinical computation layer inside hospital networks. Deterministic pipelines, auditable deployments, zero data egress.',
    postedDate: '2024-10-28',
  },
  {
    id: 'ai-research-scientist',
    title: 'AI Research Scientist',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Senior',
    description:
      'Research and develop cutting-edge approaches for medical reasoning, context modeling, and guideline-grounded computation. Publish findings and collaborate with clinical advisors.',
    postedDate: '2024-10-25',
  },
  {
    id: 'people-operations-manager',
    title: 'People Operations Manager',
    department: 'Human Resources',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Mid / Senior',
    description:
      'Shape the BloodFlow culture, lead talent acquisition, manage employee experience, and build scalable HR processes as we grow.',
    postedDate: '2024-10-30',
  },
  {
    id: 'talent-acquisition-specialist',
    title: 'Talent Acquisition Specialist',
    department: 'Human Resources',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Mid',
    description:
      'Source and recruit top talent across engineering, AI, and clinical. Build our employer brand, own the recruitment lifecycle end-to-end.',
    postedDate: '2024-10-26',
  },
  {
    id: 'junior-ai-engineer',
    title: 'Junior AI Engineer',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Junior',
    description:
      'Support AI model development, work on data preprocessing pipelines, and contribute to our medical AI research under senior guidance.',
    postedDate: '2024-10-24',
  },
];

const CareersPage = () => {
  const [jobs] = useState(initialJobs);
  const [filters, setFilters] = useState({ search: '', department: 'all', location: 'all', type: 'all' });

  const filteredJobs = useMemo(
    () =>
      jobs.filter((job) => {
        if (
          filters.search &&
          !job.title.toLowerCase().includes(filters.search.toLowerCase()) &&
          !job.description.toLowerCase().includes(filters.search.toLowerCase())
        )
          return false;
        if (filters.department !== 'all' && job.department !== filters.department) return false;
        if (filters.location !== 'all' && job.location !== filters.location) return false;
        if (filters.type !== 'all' && job.type !== filters.type) return false;
        return true;
      }),
    [jobs, filters]
  );

  const departments = ['all', ...new Set(jobs.map((job) => job.department))];
  const locations = ['all', ...new Set(jobs.map((job) => job.location))];
  const types = ['all', ...new Set(jobs.map((job) => job.type))];

  const handle = (key, value) => setFilters((p) => ({ ...p, [key]: value }));

  return (
    <div className="bf-page">
      <Navbar />
      <div style={{ height: 96 }} />

      <section className="bf-section" style={{ paddingTop: 64, paddingBottom: 40 }}>
        <div className="bf-container">
          <div className="bf-section-head">
            <span className="bf-eyebrow"><span className="dot"></span>Careers</span>
            <div>
              <h2>
                Help us make every European hospital's{' '}
                <span className="bf-grad-text">patient record computable.</span>
              </h2>
            </div>
          </div>

          <p className="bf-lead" style={{ color: 'var(--bf-ink-2)', maxWidth: '62ch' }}>
            We're a small team — clinicians, engineers, researchers — building the on-premise clinical
            intelligence layer that turns a hospital's EMR from storage into a computation system.
            Deterministic, cited, regulated. If that mandate resonates, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section style={{ paddingBottom: 8 }}>
        <div className="bf-container">
          <div
            className="bf-careers-filters"
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr',
              gap: 20,
              alignItems: 'end',
              padding: '22px 24px',
              background: 'var(--bf-surface)',
              border: '1px solid var(--bf-rule-2)',
              borderRadius: 14,
              boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.04)',
            }}
          >
            <FilterField label="Search positions">
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Search
                  size={16}
                  style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', color: 'var(--bf-muted-2)', pointerEvents: 'none' }}
                />
                <input
                  className="bf-careers-input"
                  style={{ paddingLeft: 30 }}
                  type="text"
                  value={filters.search}
                  onChange={(e) => handle('search', e.target.value)}
                  placeholder="Role or keyword"
                />
              </div>
            </FilterField>
            <FilterField label="Department">
              <select
                className="bf-careers-input"
                value={filters.department}
                onChange={(e) => handle('department', e.target.value)}
              >
                {departments.map((d) => (
                  <option key={d} value={d}>{d === 'all' ? 'All' : d}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Location">
              <select
                className="bf-careers-input"
                value={filters.location}
                onChange={(e) => handle('location', e.target.value)}
              >
                {locations.map((l) => (
                  <option key={l} value={l}>{l === 'all' ? 'All' : l}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Type">
              <select
                className="bf-careers-input"
                value={filters.type}
                onChange={(e) => handle('type', e.target.value)}
              >
                {types.map((t) => (
                  <option key={t} value={t}>{t === 'all' ? 'All' : t}</option>
                ))}
              </select>
            </FilterField>
          </div>
        </div>
      </section>

      {/* Job list */}
      <section className="bf-section" style={{ paddingTop: 56, paddingBottom: 96 }}>
        <div className="bf-container">
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 24 }}>
            <div className="bf-small">
              {filteredJobs.length} open {filteredJobs.length === 1 ? 'position' : 'positions'}
            </div>
            <div className="bf-small" style={{ color: 'var(--bf-muted-2)' }}>
              Open roles
            </div>
          </div>

          {filteredJobs.length > 0 ? (
            <div style={{ borderTop: '1px solid var(--bf-rule-2)' }}>
              {filteredJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/careers/${job.id}`}
                  className="bf-career-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '3fr 2fr auto',
                    gap: 32,
                    alignItems: 'center',
                    padding: '28px 18px',
                    borderBottom: '1px solid var(--bf-rule)',
                    textDecoration: 'none',
                    color: 'var(--bf-ink)',
                    borderRadius: 10,
                    transition: 'background 0.2s, transform 0.2s, box-shadow 0.2s',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                      <h3 style={{ fontFamily: 'var(--bf-serif)', fontSize: 24, letterSpacing: '-0.015em', margin: 0 }}>
                        {job.title}
                      </h3>
                      <span className="bf-tag">{job.level}</span>
                    </div>
                    <p
                      className="bf-body-p"
                      style={{ marginTop: 8, fontSize: 14.5, color: 'var(--bf-muted)', maxWidth: '60ch' }}
                    >
                      {job.description}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <MetaLine k="Dept" v={job.department} />
                    <MetaLine k="Location" v={job.location} />
                    <MetaLine k="Type" v={job.type} />
                  </div>
                  <div
                    className="bf-small bf-view-role"
                    style={{
                      color: 'var(--bf-muted)',
                      letterSpacing: '0.06em',
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    View role <span style={{ fontFamily: 'var(--bf-mono)' }}>→</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div
              style={{
                padding: '64px 32px',
                textAlign: 'center',
                border: '1px solid var(--bf-rule-2)',
                borderRadius: 14,
                background: 'var(--bf-surface)',
              }}
            >
              <div className="bf-small" style={{ marginBottom: 12 }}>No results</div>
              <h3>No positions match those filters.</h3>
              <p className="bf-body-p" style={{ marginTop: 8, color: 'var(--bf-muted)' }}>
                Try broadening your search, or check back in a couple of weeks.
              </p>
            </div>
          )}

          {/* Open application CTA */}
          <div
            style={{
              marginTop: 64,
              padding: '32px 36px',
              border: '1px solid color-mix(in oklab, var(--bf-accent) 28%, transparent)',
              borderRadius: 16,
              background: 'linear-gradient(120deg, var(--bf-signal-bg) 0%, var(--bf-surface) 65%)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 24,
              boxShadow: '0 28px 52px -34px rgba(240,31,61,0.28), 0 8px 22px -14px rgba(20,24,30,0.08)',
            }}
          >
            <div>
              <div className="bf-small">Don't see a fit?</div>
              <h3 style={{ marginTop: 6 }}>Send us an open application.</h3>
              <p className="bf-body-p" style={{ marginTop: 6, fontSize: 14, color: 'var(--bf-muted)' }}>
                Clinical advisors, medical device specialists, senior designers — we're always interested.
              </p>
            </div>
            <a href="mailto:support@bloodflow.ai" className="bf-btn bf-btn-primary">
              Email us <span className="bf-arrow">→</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .bf-careers-input {
          width: 100%;
          font-family: var(--bf-sans);
          font-size: 14.5px;
          color: var(--bf-ink);
          background: transparent;
          border: none;
          border-bottom: 1px solid var(--bf-rule-2);
          padding: 10px 0;
          outline: none;
          transition: border-color 0.15s;
        }
        .bf-careers-input::placeholder { color: var(--bf-muted-2); }
        .bf-careers-input:focus { border-bottom-color: var(--bf-accent); }
        select.bf-careers-input { appearance: none; background-image: linear-gradient(45deg, transparent 50%, var(--bf-muted) 50%), linear-gradient(135deg, var(--bf-muted) 50%, transparent 50%); background-position: calc(100% - 14px) center, calc(100% - 9px) center; background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; padding-right: 30px; cursor: pointer; color-scheme: dark; }
        select.bf-careers-input option { background: var(--bf-surface); color: var(--bf-ink); }

        .bf-career-row:hover {
          background: var(--bf-surface);
          transform: translateY(-1px);
          box-shadow: 0 18px 36px -22px rgba(0,0,0,0.55);
        }
        .bf-career-row:hover .bf-view-role { color: var(--bf-accent); transform: translateX(3px); }
        .bf-view-role { transition: color 0.18s, transform 0.18s; }

        @media (max-width: 900px) {
          .bf-careers-filters { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
          .bf-career-row { grid-template-columns: 1fr !important; gap: 16px !important; align-items: start !important; }
        }
        @media (max-width: 560px) {
          .bf-careers-filters { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

const FilterField = ({ label, children }) => (
  <label style={{ display: 'block' }}>
    <div className="bf-caption" style={{ marginBottom: 4, color: 'var(--bf-muted)' }}>
      {label}
    </div>
    {children}
  </label>
);

const MetaLine = ({ k, v }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 8, alignItems: 'baseline' }}>
    <span className="bf-caption" style={{ color: 'var(--bf-muted-2)' }}>{k}</span>
    <span style={{ fontFamily: 'var(--bf-sans)', fontSize: 13, color: 'var(--bf-ink-2)' }}>{v}</span>
  </div>
);

export default CareersPage;
